import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import { tokenPromptService } from '../../../utils/localDevUtils';

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  hint: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

/**
 * Listens for 401-triggered token prompt requests from the axios interceptor
 * (via tokenPromptService) and shows a dialog for developers to enter their
 * local dev tokens without opening browser DevTools.
 *
 * Only rendered in frontendOnly mode — mount this once near the root of the app.
 */
const TokenRefreshDialog: React.FC = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [backstageToken, setBackstageToken] = useState('');
  const [msToken, setMsToken] = useState('');

  useEffect(() => {
    const unsubscribe = tokenPromptService.subscribe(() => {
      // Pre-fill with whatever is already stored so users can see/edit existing values.
      setBackstageToken(
        window.localStorage.getItem('devx_backstage_token') ?? '',
      );
      setMsToken(window.localStorage.getItem('devx_ms_token') ?? '');
      setOpen(true);
    });
    return unsubscribe;
  }, []);

  const handleSave = () => {
    if (!backstageToken.trim() || !msToken.trim()) return;
    window.localStorage.setItem('devx_backstage_token', backstageToken.trim());
    window.localStorage.setItem('devx_ms_token', msToken.trim());
    setOpen(false);
    tokenPromptService.resolve();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>Developer tokens required</DialogTitle>
      <DialogContent>
        <DialogContentText>
          A request returned <strong>401 Unauthorized</strong>. Enter your local
          dev tokens below — they will be saved to <code>localStorage</code> and
          the request will be retried automatically.
        </DialogContentText>

        <TextField
          className={classes.field}
          label="devx_backstage_token"
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          variant="outlined"
          value={backstageToken}
          onChange={e => setBackstageToken(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          inputProps={{ 'aria-label': 'devx_backstage_token' }}
        />
        <p className={classes.hint}>
          Backstage identity token — copy from UAT DevTools &gt; Application
          &gt; Local Storage.
        </p>

        <TextField
          className={classes.field}
          label="devx_ms_token"
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          variant="outlined"
          value={msToken}
          onChange={e => setMsToken(e.target.value)}
          onKeyDown={handleKeyDown}
          inputProps={{ 'aria-label': 'devx_ms_token' }}
        />
        <p className={classes.hint}>
          Microsoft access token — copy from UAT DevTools &gt; Application &gt;
          Local Storage.
        </p>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!backstageToken.trim() || !msToken.trim()}
        >
          Save &amp; retry
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokenRefreshDialog;
